/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Row, Cell, TextField } from 'sarsaparilla';
import get from 'lodash/get';
import SelectInput from './inputs/SelectInput';
import { PassTypeProps, WatercraftInfoProps, WatercraftInfoDefaults } from '../props';

export default class WatercraftInfoForm extends Component {
    static defaultProps = {
        watercraftInfo: WatercraftInfoDefaults,
    };

    static propTypes = {
        isScannedPass: PropTypes.bool,
        number: PropTypes.number,
        submitAttempted: PropTypes.bool,
        watercraftInfo: WatercraftInfoProps,
        passType: PassTypeProps,
        onChange: PropTypes.func.isRequired,
    };

    constructor(props) {
        super(props);

        this.state = {
            watercraftType: this.props.watercraftInfo.type,
            licenseNumber: get(this.props, 'watercraftInfo.fields.license', ''),
        };
    }

    componentDidMount() {
        this.props.onChange(
            {
                ...this.props.watercraftInfo,
                fields: { license: this.state.licenseNumber },
            },
            this.formIsValid()
        );
    }

    componentWillUnmount() {
        this.props.onChange(this.props.watercraftInfo, true);
    }

    onWatercraftTypeChanged = (newWatercraftType) => {
        const { watercraftInfo, watercraftType, licenseNumber } = this.state;
        if (newWatercraftType !== watercraftType) {
            const newLicenseNumber = this.getDefaultLicenseNumber(newWatercraftType);
            const licenseNum =
                newLicenseNumber !== licenseNumber ? newLicenseNumber : licenseNumber;
            this.setState(
                {
                    watercraftType: newWatercraftType,
                    licenseNumber: licenseNum,
                },
                () => {
                    this.props.onChange(
                        {
                            ...watercraftInfo,
                            type: newWatercraftType,
                            fields: { license: licenseNum },
                        },
                        this.formIsValid()
                    );
                }
            );
        }
    };

    onLicenseNumberChanged = (licenseNumber) => {
        const { watercraftInfo } = this.props;

        this.setState(
            (prevState) => ({ ...prevState, licenseNumber }),
            () => {
                this.props.onChange(
                    { ...watercraftInfo, fields: { license: licenseNumber } },
                    this.formIsValid()
                );
            }
        );
    };

    getDefaultLicenseNumber = (watercraftType) => {
        const { license_required } = get(
            this.props,
            `passType.features['equipment-info']['watercraft']['${watercraftType}']`,
            {}
        );
        return license_required ? '' : 'N/A';
    };

    isMissingLicenseNumber(number, watercraftType) {
        const licenseRequired = get(this.props.passType, [
            'features',
            'equipment-info',
            'watercraft',
            watercraftType,
            'license_required',
        ]);

        if (!licenseRequired) {
            return false;
        }

        return number === '';
    }

    formIsValid() {
        const { watercraftType, licenseNumber } = this.state;
        return !this.isMissingLicenseNumber(licenseNumber, watercraftType);
    }

    render() {
        const { isScannedPass, number, passType, submitAttempted } = this.props;
        const watercraftTypes = get(passType, [
            'features',
            'equipment-info',
            'watercraft',
        ]);
        if (!watercraftTypes) {
            return null;
        }
        let { watercraftType } = this.state;
        // if watercraftType is empty or doesn't exist (probably because it is the default value), set it to the first type
        if (!watercraftType || !watercraftTypes[watercraftType]) {
            watercraftType = Object.keys(watercraftTypes)[0];
        }
        this.onWatercraftTypeChanged(watercraftType);
        const { licenseNumber } = this.state;

        const licenseRequired = get(
            watercraftTypes,
            `${watercraftType}.license_required`
        );

        return (
            <Row className={isScannedPass ? 'scanned-pass' : ''}>
                <Cell width={6}>
                    <SelectInput
                        required
                        label="Boat Type"
                        id={`watercraft-type-${number}`}
                        value={watercraftType}
                        options={Object.entries(watercraftTypes).map(
                            ([value, { name: label }]) => ({ label, value })
                        )}
                        onChange={this.onWatercraftTypeChanged}
                    />
                </Cell>
                <Cell width={6}>
                    <TextField
                        isRequired={licenseRequired}
                        label="Boat Registration Number"
                        id={`watercraft-license-${number}`}
                        value={licenseNumber}
                        disabled={!licenseRequired}
                        shouldValidateOnUpdate={submitAttempted}
                        onChange={(e) => this.onLicenseNumberChanged(e.target.value)}
                    />
                </Cell>
            </Row>
        );
    }
}
